<template>
    <a-modal
        :visible="true"
        width="80vw"
        :footer="null"
        :closable="false"
        :destroy-on-close="true"
        centered
        wrap-class-name="cwm-wrap"
        @cancel="$emit('close')"
    >
        <div class="cwm-modal">
            <div class="cwm-head">
                <div>
                    <h3>Alterar vencedor</h3>
                    <div class="cwm-sub">
                        {{ product.name }}<template v-if="storeId"> · {{ storeName || `Loja ${storeId}` }}</template>
                    </div>
                    <div class="cwm-badges">
                        <span class="cwm-badge cwm-badge-store" v-if="storeId">Loja {{ storeName || storeId }}</span>
                        <span class="cwm-badge">EAN {{ product.ean || "—" }}</span>
                        <span class="cwm-badge">Cód. interno {{ product.internal_code || "—" }}</span>
                        <span class="cwm-badge">Embalagem {{ product.packing || "—" }}</span>
                    </div>
                </div>
                <button class="cwm-x" @click="$emit('close')">
                    <span class="material-icons-outlined">close</span>
                </button>
            </div>

            <cc-loader-full v-if="is_loading" />
            <div class="cwm-body" v-else>
                <div class="cwm-store-block" v-for="(store, index) in stores" :key="store.store_id">
                    <div class="cwm-store-name" v-if="!storeId">{{ store.store_name || `Loja ${store.store_id}` }}</div>

                    <OffersTable
                        ref="offersTable"
                        :record="store.record"
                        :offers="store.offers"
                        :show-actions="false"
                        :billing="billingFor(store)"
                        :quotation-id="quotationId"
                        :store-id="store.store_id"
                        @toggle-winner="onToggleWinner(store, $event)"
                        @navigate-out="focusNextStore(index, $event)"
                    />
                </div>

                <p v-if="!stores.length" class="cwm-empty">Nenhuma loja com ofertas para este produto.</p>
            </div>

            <div class="cwm-foot">
                <button class="cwm-btn-ghost" @click="$emit('close')">Fechar</button>
            </div>
        </div>
    </a-modal>
</template>

<script>
import QuotationService from "@/services/QuotationService";
import RequestService from "@/services/v3/request.service";
import ErrorHandlerService from "@/services/ErrorHandlerService";
import OffersTable from "@/pages/monitors/winners-v2/components/comparativo/_offers.table.vue";

export default {
    name: "ChangeWinnerModal",
    components: { OffersTable },
    props: {
        product: { type: Object, required: true },
        quotationId: [String, Number],
        storeId: [String, Number],
        storeName: String,
        preOrders: { type: Array, default: () => [] },
    },
    data() {
        return {
            svc: new QuotationService(),
            v3Svc: new RequestService(),
            stores: [],
            is_loading: true,
        };
    },
    computed: {
        requestProductId() {
            return this.product.main_product_id || this.product.id;
        },
        productUnits() {
            return this.packingUnits(this.product);
        },
        sellerMinimums() {
            const minimums = {};

            this.preOrders.forEach((row) => {
                if (Number(row.minimum_billing) > 0) minimums[row.seller_id] = Number(row.minimum_billing);
            });

            return minimums;
        },
    },
    created() {
        this.load();
    },
    methods: {
        packingUnits(row) {
            if (row.is_generic != 0 || row.main_product_id != null) return 1;
            return parseInt((row.packing || "").split(" ")[1]) || 1;
        },
        billingFor(store) {
            const billing = {};

            store.offers.forEach((offer) => {
                const others = this.preOrders
                    .filter((row) => row.store_id == store.store_id && row.seller_id == offer.idVendedor && row.id != offer.pro_id)
                    .reduce((sum, row) => sum + (Number(row.requested_quantity) || 0) * this.packingUnits(row) * (Number(row.offer_price) || 0), 0);

                const minimum = Number(offer.minimum_billing) > 0
                    ? Number(offer.minimum_billing)
                    : this.sellerMinimums[offer.idVendedor] || null;

                billing[offer.idVendedor] = {
                    current: others + (Number(offer.quantidade) || 0) * this.productUnits * (Number(offer.valor) || 0),
                    minimum,
                };
            });

            return billing;
        },
        focusNextStore(index, step) {
            const tables = this.$refs.offersTable || [];

            for (let i = index + step; i >= 0 && i < this.stores.length; i += step) {
                const table = tables.find((t) => t.storeId === this.stores[i].store_id);

                if (table && table.activateEdge(step)) return;
            }
        },
        loadWinnerQuantities(offers) {
            const sellerProducts = {};

            offers.filter((o) => o.is_winner).forEach((o) => {
                sellerProducts[o.seller_id] = o.id || this.product.id;
            });

            return Promise.all(
                Object.keys(sellerProducts).map((sellerId) =>
                    this.svc
                        .getLojasQuantidade(sellerProducts[sellerId], this.quotationId, sellerId)
                        .then((response) => ({ sellerId, rows: response.data.produtos || [] }))
                )
            ).then((results) => {
                const map = {};

                results.forEach(({ sellerId, rows }) => {
                    rows.forEach((row) => {
                        map[`${row.codLoja}-${sellerId}`] = row;
                    });
                });

                return map;
            });
        },
        buildStoreOffers(rows) {
            const sellers = {};
            const storeRows = {};
            const stores = {};

            rows.forEach((row) => {
                if (!sellers[row.seller_id]) sellers[row.seller_id] = row;
                storeRows[`${row.store_id}-${row.seller_id}`] = row;
                if (!stores[row.store_id]) stores[row.store_id] = { store_id: row.store_id, store_name: row.store_name };
            });

            const targetStores = this.storeId
                ? [stores[this.storeId] || { store_id: this.storeId, store_name: this.storeName }]
                : Object.values(stores);

            const sellerList = Object.values(sellers).sort((a, b) => (a.offer_price || 0) - (b.offer_price || 0));

            return targetStores.map((store) => ({
                ...store,
                rows: sellerList.map(
                    (seller) =>
                        storeRows[`${store.store_id}-${seller.seller_id}`] || {
                            ...seller,
                            store_id: store.store_id,
                            store_name: store.store_name,
                            is_winner: false,
                            quantity_to_buy: 0,
                            has_order: false,
                        }
                ),
            }));
        },
        load() {
            this.is_loading = true;

            this.v3Svc
                .request_offers_by_product_per_store(this.quotationId, this.requestProductId)
                .then((response) => {
                    const rows = response.data || [];
                    const stores = this.buildStoreOffers(rows);
                    const offers = stores.flatMap((store) => store.rows);

                    // LEGACY: kyojin antiga não manda quantity_to_buy/has_order no /offers/by-store; busca a quantidade no getLojasQuantidade. Remover quando a kyojin com esses campos estiver em produção.
                    if (rows.length && !("quantity_to_buy" in rows[0])) {
                        return this.loadWinnerQuantities(offers).then((quantities) => ({ stores, quantities }));
                    }

                    return { stores, quantities: null };
                })
                .then(({ stores, quantities }) => {
                    this.stores = stores.map((store) => ({
                        store_id: store.store_id,
                        store_name: store.store_name,
                        record: {
                            flag_generico: 1,
                            cot_condicao_pagamento: null,
                            last_purchase: null,
                        },
                        offers: store.rows.map((offer) => {
                            // LEGACY: quantidade do getLojasQuantidade quando a kyojin é antiga (quantities != null). Remover junto com o fallback do load().
                            const storeQuantity = quantities && offer.is_winner ? quantities[`${offer.store_id}-${offer.seller_id}`] : null;
                            const legacyQuantity = storeQuantity ? Number(storeQuantity.quantidadeSugestao) || 0 : 0;

                            return {
                                idVendedor: offer.seller_id,
                                pro_id: offer.id || this.product.id,
                                nomeFornecedor: offer.provider_name,
                                nomeVendedor: offer.seller_name,
                                pro_observacao: offer.brand,
                                forma: offer.payment_condition,
                                embalagem: offer.packing,
                                valor: offer.offer_price,
                                observacao: offer.observations,
                                vencedor_quantidade: offer.is_winner ? 1 : 0,
                                quantidade: !offer.is_winner ? 0 : quantities ? legacyQuantity : Number(offer.quantity_to_buy) || 0,
                                qtdPedido: offer.has_order ? 1 : 0,
                                minimum_billing: offer.minimum_billing,
                            };
                        }),
                    }));
                    this.is_loading = false;
                })
                .catch((error) => {
                    this.is_loading = false;
                    ErrorHandlerService.handle(error, this.$store);
                });
        },
        onToggleWinner(store, { winner }) {
            const nextState = !(winner.vencedor_quantidade > 0);
            this.$set(winner, "is_loading", true);

            this.svc
                .toggleQuotationWinner(this.quotationId, store.store_id, winner.pro_id, winner.idVendedor, nextState)
                .then(() => {
                    this.$set(winner, "is_loading", false);
                    this.$set(winner, "vencedor_quantidade", nextState ? 1 : 0);
                    this.$emit("saved");
                    this.load();
                })
                .catch((error) => {
                    this.$set(winner, "is_loading", false);
                    ErrorHandlerService.handle(error, this.$store);
                });
        },
    },
};
</script>

<style lang="scss" scoped>
.cwm-modal {
    max-height: 82vh;
    background: #fff;
    display: flex;
    flex-direction: column;
    overflow: hidden;
}
.cwm-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    padding: 18px 20px 14px;

    h3 {
        font-size: 16px;
        font-weight: 600;
        color: #262626;
    }
}
.cwm-sub {
    font-size: 13px;
    color: #8c8c8c;
    margin-top: 2px;
}
.cwm-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin-top: 8px;
}
.cwm-badge {
    display: inline-block;
    padding: 3px 10px;
    border-radius: 12px;
    background: #f5f5f5;
    color: #595959;
    font-size: 12px;
    font-weight: 500;
    white-space: nowrap;
}
.cwm-badge-store {
    background: var(--primary-color-opacity, rgba(232, 96, 44, 0.08));
    color: var(--primary-color);
    font-weight: 600;
}
.cwm-x {
    width: 28px;
    height: 28px;
    border: none;
    background: none;
    border-radius: 6px;
    color: #8c8c8c;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
        background: #f0f0f0;
        color: #262626;
    }
}
.cwm-body {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 8px 20px 4px;
    border-top: 1px solid #f0f0f0;
    min-height: 160px;
}
.cwm-store-block {
    padding: 14px 0;
    border-bottom: 1px solid #f5f5f5;

    &:last-child {
        border-bottom: none;
    }
}
.cwm-store-name {
    font-size: 14px;
    font-weight: 600;
    color: #262626;
    margin-bottom: 8px;
}
.cwm-empty {
    text-align: center;
    color: #8c8c8c;
    font-size: 13px;
    padding: 24px 0;
}
.cwm-foot {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    padding: 14px 20px;
    border-top: 1px solid #f0f0f0;
}
.cwm-btn-ghost {
    height: 40px;
    padding: 0 18px;
    border-radius: 10px;
    border: 1px solid #d9d9d9;
    background: #fff;
    color: #434343;
    font-weight: 500;
    font-size: 14px;
    cursor: pointer;

    &:hover {
        border-color: var(--primary-color);
        color: var(--primary-color);
    }
}
</style>

<style lang="scss">
.cwm-wrap {
    .ant-modal {
        // vw-driven so it scales with the viewport instead of a fixed px size —
        // needs to stay usable on low-res desktops/PDV terminals (1366x768 is
        // common among our users), not just on large monitors.
        min-width: 560px;
        max-width: 1140px;
    }

    .ant-modal-content {
        padding: 0;
        border-radius: 10px;
        overflow: hidden;
    }

    .ant-modal-body {
        padding: 0;
    }
}
</style>
